import React, {useState} from "react";
import {IssueApiJson} from "./IssueApiJson";
import {Protected} from "../../layout/Protected";
import {OptiselModal} from "../../components/modal/OptiselModal";
import AjaxService from "../../platform/ajax/AjaxService";

/**
 * Duży baner na górze strony zarchiwizowanej sprawy. "Pobierz ZIP" tylko dla spraw zamkniętych
 * jako zapytanie/zamówienie (closureType != null) — zwykła archiwizacja nie ma eksportu.
 */
export function IssueArchivedBanner(props: { issue: IssueApiJson, restoreFromArchive: () => Promise<void> }) {
    const issue = props.issue;
    return <div className="alert alert-warning d-flex align-items-center mb-4 p-5">
        <div className={"flex-grow-1"}>
            <div className="fs-3 fw-boldest"><i className="fas fa-archive me-2"/>Sprawa znajduje się w archiwum</div>
            {closureTypeDescription(issue.closureType) && <div className="fw-bold mt-1">{closureTypeDescription(issue.closureType)}</div>}
            {issue.archiveReason && <div className="mt-1">Powód/opis: {issue.archiveReason}</div>}
        </div>
        <div className={"d-flex align-items-center justify-content-end gap-2"}>
            {issue.closureType != null && <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
                <IssueArchiveZipDownloadButton issueId={issue.id}/>
            </Protected>}
            <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
                <IssueRestoreFromArchiveButton issueName={issue.name} restoreFromArchive={props.restoreFromArchive}/>
            </Protected>
        </div>
    </div>;
}

function closureTypeDescription(closureType: string | null): string | null {
    if (closureType === "inquiry") {
        return "Zamknięta jako zapytanie";
    }
    if (closureType === "order") {
        return "Zamknięta jako zamówienie";
    }
    return null;
}

function IssueArchiveZipDownloadButton(props: { issueId: string }) {
    const [downloading, setDownloading] = useState(false);

    const download = async () => {
        setDownloading(true);
        try {
            // Właściwa nazwa pliku (zapytanie-/sprawa-<nr>.zip) przychodzi w Content-Disposition
            await AjaxService.downloadAuthenticatedFile(`/api/issue/${props.issueId}/download-zip`, `sprawa-${props.issueId}.zip`);
        } catch (e) {
            window.alert("Nie udało się pobrać archiwum ZIP sprawy.");
        } finally {
            setDownloading(false);
        }
    };

    return <button className={"btn btn-primary fw-bolder"} disabled={downloading} onClick={download}>
        {downloading ? <><i className="fas fa-spinner fa-spin me-2"/>Pobieranie…</> : <><i className="fas fa-file-archive me-2"/>Pobierz ZIP</>}
    </button>;
}

function IssueRestoreFromArchiveButton(props: { issueName: string, restoreFromArchive: () => Promise<void> }) {
    const [confirming, setConfirming] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const performRestore = () => {
        setRestoring(true);
        props.restoreFromArchive().catch(() => {
            window.alert("Nie udało się przywrócić sprawy z archiwum. Spróbuj ponownie.");
            setRestoring(false);
        });
    };

    return <>
        <button className={"btn btn-sm btn-light-primary"} onClick={() => setConfirming(true)}>Wyciągnij z archiwum</button>
        <OptiselModal opened={confirming} header={"Przywrócenie z archiwum"} closeable={false} onClose={() => setConfirming(false)}>
            <p>Czy przywrócić sprawę <strong>{props.issueName}</strong> z archiwum? Sprawa wróci do normalnego obiegu.</p>
            <div className={"d-flex gap-2 justify-content-end"}>
                <button className={"btn btn-secondary"} onClick={() => setConfirming(false)} disabled={restoring}>Anuluj</button>
                <button className={"btn btn-primary"} onClick={performRestore} disabled={restoring}>
                    {restoring ? <><i className="fas fa-spinner fa-spin me-2"/>Przywracanie…</> : "Przywróć"}
                </button>
            </div>
        </OptiselModal>
    </>;
}
